import type { JobListQuery } from '@chukua/common';
import { Button, Tag, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import { DEADLINE_PRESSURE_LABELS, deadlinePressureLabel } from '../../../shared/policies/deadline-pressure-labels';
import { formatMoney } from '../../../shared/policies/format-money';
import { RATE_BASIS_LABELS } from '../../../shared/policies/rate-basis-labels';
import type { JobListItem } from '../../jobs/jobs.api';
import { jobPath } from '../../jobs/jobs.paths';
import type { JobTypeOption } from '../hooks/use-job-type-options';

type QueueList = Pick<ListQueryControls<JobListQuery>, 'sortOrderFor' | 'filteredValueFor'>;

export interface JobQueueActions {
  skippedIds: string[];
  assigningJobId: string | null;
  failedJobId: string | null;
  onAssign: (jobId: string) => void;
  onSkip: (jobId: string) => void;
  onUnskip: (jobId: string) => void;
}

interface BuildJobQueueColumnsArgs {
  jobTypeOptions: JobTypeOption[];
  list: QueueList;
  actions: JobQueueActions;
}

const formatDate = (date: string | null) => (date ? new Date(date).toLocaleDateString() : '—');

/** "Mombasa Port · 12/3/2026": where and when the cargo is collected. */
function pickup(job: JobListItem): string {
  return [job.originZoneName ?? '—', formatDate(job.requestedDate)].join(' · ');
}

/** "KES 120,000 / trip": the price agreed with the customer, in the unit it was agreed per. */
function agreedPrice(job: JobListItem): string {
  if (!job.agreedPrice) return '—';
  const basis = job.rateBasis ? ` / ${RATE_BASIS_LABELS[job.rateBasis].replace(/^Per /, '').toLowerCase()}` : '';
  return `${formatMoney(job.agreedPrice)}${basis}`;
}

function buildJobColumns({ jobTypeOptions, list }: Omit<BuildJobQueueColumnsArgs, 'actions'>): TableColumnsType<JobListItem> {
  return [
    { title: 'Customer', key: 'customer', render: (_, job) => <span className="font-medium">{job.customerName}</span> },
    {
      title: 'Cargo',
      key: 'jobTypeCode',
      filters: jobTypeOptions.map((option) => ({ text: option.label, value: option.value })),
      filteredValue: list.filteredValueFor('jobTypeCode'),
      render: (_, job) => job.jobTypeName,
    },
    { title: 'Pickup', key: 'pickup', render: (_, job) => pickup(job) },
    { title: 'Delivery', key: 'delivery', render: (_, job) => job.destinationZoneName ?? '—' },
    {
      title: 'Deadline',
      key: 'deadline',
      sorter: true,
      sortOrder: list.sortOrderFor('deadline'),
      render: (_, job) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {formatDate(job.customerDeliveryDeadline)}
          {job.deadlinePressure && (
            <Tag color={DEADLINE_PRESSURE_LABELS[job.deadlinePressure].color}>{deadlinePressureLabel(job.deadlinePressure, job.daysUntilDeadline)}</Tag>
          )}
        </span>
      ),
    },
    { title: 'Readiness', key: 'readiness', render: (_, job) => <Tag color={job.ready ? 'success' : 'default'}>{job.ready ? 'Ready' : 'Not ready'}</Tag> },
    { title: 'Agreed price', key: 'agreedPrice', render: (_, job) => agreedPrice(job) },
  ];
}

function buildActionsColumn({ skippedIds, assigningJobId, failedJobId, onAssign, onSkip, onUnskip }: JobQueueActions): TableColumnsType<JobListItem>[number] {
  return {
    title: 'Actions',
    key: 'actions',
    render: (_, job) => (
      <div className="flex flex-col items-start gap-1">
        <div className="flex flex-wrap gap-2">
          <Button type="primary" size="small" loading={assigningJobId === job.id} onClick={() => onAssign(job.id)}>
            Assign
          </Button>
          <Link to={jobPath(job.id)}>
            <Button size="small">View details</Button>
          </Link>
          {skippedIds.includes(job.id) ? (
            <Button size="small" type="text" onClick={() => onUnskip(job.id)}>
              Unskip
            </Button>
          ) : (
            <Button size="small" type="text" onClick={() => onSkip(job.id)}>
              Skip
            </Button>
          )}
        </div>
        {failedJobId === job.id && (
          <span role="alert" className="text-danger text-xs">
            Couldn't check this job's trips. Try again.
          </span>
        )}
      </div>
    ),
  };
}

/** Each column's `key` is its sort field or filter param in `jobListQuerySchema`; profit margin is absent because the platform holds no cost per job. */
export function buildJobQueueColumns({ jobTypeOptions, list, actions }: BuildJobQueueColumnsArgs): TableColumnsType<JobListItem> {
  return [...buildJobColumns({ jobTypeOptions, list }), buildActionsColumn(actions)];
}
